import React, { useState } from 'react'
import classnames from 'classnames'
import Caret from 'components/Caret/Caret'
import { FilterProp } from 'frontend/types'
import { Direction, KeyCodes } from 'frontend/enums'
import styles from './FilterDropdown.module.css'
import useClickOutside from 'frontend/utils/hooks/useClickOutside'
import useKeys from 'frontend/utils/hooks/useKeys'
import FilterDropdownItem from './components/FilterDropdownItem'

interface Props {
  activeLabels?: string[]
  label: string
  onOptionSelect: (option: FilterProp) => void
  options: FilterProp[]
  isSingleChoice?: boolean
  isOnMobile?: boolean
  dataTestId?: string
}

const FilterDropdown = ({
  activeLabels,
  label,
  onOptionSelect,
  options,
  isSingleChoice = false,
  isOnMobile = false,
  dataTestId = 'toggle-button',
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const caretDirection = isOpen ? Direction.Up : Direction.Down

  const insideRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false))

  useKeys(isOpen, {
    [KeyCodes.ESCAPE_KEY]: () => setIsOpen(false),
  })

  return (
    <div className={styles.panelContainer} ref={insideRef}>
      <button
        aria-expanded={isOpen}
        className={styles.button}
        data-testid={dataTestId}
        onClick={() => setIsOpen(prevState => !prevState)}
        type="button"
      >
        <span className={styles.label}>{label}</span>
        <Caret direction={caretDirection} styling={styles.caret} />
      </button>
      <div
        aria-hidden={!isOpen}
        data-testid="dropdown-container"
        className={classnames(styles.optionsWrapper, {
          [`${styles.expanded}`]: isOpen,
        })}
      >
        <ul
          className={classnames(styles.optionsContainer, {
            [`${styles.expanded}`]: isOpen,
            [`${styles.collapsed}`]: !isOpen,
          })}
        >
          {options.map((option, index) => {
            const isSelected = activeLabels?.includes(option.label)
            return (
              <FilterDropdownItem
                index={index}
                key={index}
                isSelected={Boolean(isSelected)}
                option={option}
                onOptionSelect={onOptionSelect}
                isOpen={isOpen}
                isSingleChoice={isSingleChoice}
                isOnMobile={isOnMobile}
              />
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default FilterDropdown
